import { useEffect, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Info, Trash2 } from "lucide-react";

import { lifecycleService, type PromotionCriteria, type StudentPromotion } from "../../services/lifecycleService";
import { apiErrorMessage } from "../../utils/apiError";
import AIAssistField from "../AIAssistField";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { ReasonPanel } from "../inventory/InventoryParts";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { Blockers, Checks, LearnerHistory } from "./LifecycleParts";
import { day, pct, promotionBlockers, promotionChecks, promotionConfig, promotionStatus } from "./lifecycleLogic";

const guardianText = (row: StudentPromotion, criteria?: PromotionCriteria) =>
  !criteria?.requires_parent_approval ? "Not needed" : row.parent_approved ? `Agreed ${day(row.parent_approval_date)}` : "Waiting for the guardian";

/** Promotion proposals: propose a class from its records, then approve or reject each learner. */
export function PromotionsPanel({ rows, criteria, canManage, isLoading, isError, onRetry, onChanged, initialView, proposing, onPropose, onCloseProposal }: {
  rows?: StudentPromotion[];
  criteria: PromotionCriteria[];
  canManage: boolean;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onChanged: () => void;
  initialView?: string;
  proposing: boolean;
  onPropose: () => void;
  onCloseProposal: () => void;
}) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const criteriaOf = (row: StudentPromotion) => criteria.find((item) => item.id === row.criteria);
  const approve = useMutation({
    mutationFn: (row: StudentPromotion) => lifecycleService.approvePromotion(row.id),
    onSuccess: (_, row) => { setViewing(null); setNotice({ tone: "success", text: `${row.student_name} promoted to ${row.to_class_name}.` }); onChanged(); },
  });
  const reject = useMutation({
    mutationFn: ({ row, reason }: { row: StudentPromotion; reason: string }) => lifecycleService.rejectPromotion(row.id, reason),
    onSuccess: (_, { row }) => { setViewing(null); setNotice({ tone: "success", text: `${row.student_name}'s promotion was rejected.` }); onChanged(); },
  });
  const remove = useMutation({
    mutationFn: (row: StudentPromotion) => lifecycleService.deleteStudentPromotion(row.id),
    onSuccess: (_, row) => { setViewing(null); setNotice({ tone: "success", text: `The proposal for ${row.student_name} was deleted.` }); onChanged(); },
    onError: (error) => setNotice({ tone: "error", text: apiErrorMessage(error, "The proposal could not be deleted.") }),
  });

  const columns: RegisterColumn<StudentPromotion>[] = [
    { header: "Learner", cell: (row) => <span><span className="font-medium text-gray-900">{row.student_name}</span> <span className="text-xs text-gray-500">{row.student_id}</span></span>, exportValue: (row) => row.student_name },
    { header: "Student ID", cell: (row) => row.student_id, exportValue: (row) => row.student_id, exportOnly: true },
    { header: "From", cell: (row) => row.from_class_name, exportValue: (row) => row.from_class_name },
    { header: "To", cell: (row) => row.to_class_name, exportValue: (row) => row.to_class_name },
    { header: "Attendance", cell: (row) => pct(row.attendance_percentage), exportValue: (row) => Number(row.attendance_percentage), align: "right" },
    { header: "Average", cell: (row) => pct(row.average_grade), exportValue: (row) => Number(row.average_grade), align: "right" },
    { header: "Incidents", cell: (row) => row.behavior_incidents, exportValue: (row) => row.behavior_incidents, align: "right" },
    { header: "Criteria", cell: (row) => (row.meets_criteria ? <span className="text-emerald-700">Meets</span> : <span className="text-red-700">Falls short</span>), exportValue: (row) => (row.meets_criteria ? "Meets" : "Falls short") },
    { header: "Guardian", cell: (row) => guardianText(row, criteriaOf(row)), exportValue: (row) => guardianText(row, criteriaOf(row)), exportOnly: true },
    { header: "Status", cell: (row) => <StatusDot {...promotionStatus(row.status)} />, exportValue: (row) => promotionStatus(row.status).label },
    { header: "Proposed", cell: (row) => day(row.created_at), exportValue: (row) => day(row.created_at), exportOnly: true },
  ];
  const row = viewing ? rows?.find((item) => item.id === viewing) : undefined;
  const rowCriteria = row && criteriaOf(row);
  const blockers = row ? promotionBlockers(row, rowCriteria) : [];

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        key={initialView}
        title="Promotions"
        description="Each learner proposed to move up a class, with their year's attendance, average and incidents against the criteria. Approving moves the learner into the new class."
        actions={canManage && <button type="button" className="btn btn-primary h-9 text-sm" onClick={onPropose}>Propose a class</button>}
        rows={rows}
        config={promotionConfig}
        initialState={initialView ? { view: initialView } : undefined}
        columns={columns}
        rowKey={(item) => item.id}
        onOpen={(item) => setViewing(item.id)}
        openLabel={(item) => (canManage && item.status === "pending" ? "Decide" : "Open")}
        noun={["promotion", "promotions"]}
        exportName={{ file: "promotions", title: "Promotion Proposals" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No promotions have been proposed. Propose a class to start."
      />

      {row && (
        <DetailsModal
          title={row.student_name}
          headline={{ label: "Promotion", value: `${row.from_class_name} → ${row.to_class_name}`, note: `${row.academic_year_name} · proposed ${day(row.created_at)}` }}
          status={promotionStatus(row.status)}
          facts={[
            ["Student ID", row.student_id],
            ["Criteria", rowCriteria?.name ?? "—"],
            ["Guardian", guardianText(row, rowCriteria)],
            ["Decided by", row.approved_by_name || "—"],
            ["Promoted on", day(row.promotion_date) || "—"],
            ["Academic year", row.academic_year_name],
          ]}
          sections={[{ title: "Notes", body: row.notes, tone: row.status === "rejected" ? "danger" : "plain" }]}
          onClose={() => setViewing(null)}
          footer={
            <>
              {canManage && row.status === "pending" && (
                <button type="button" disabled={remove.isPending} onClick={() => window.confirm(`Delete the proposal for ${row.student_name}?`) && remove.mutate(row)} className="mr-auto inline-flex items-center gap-1.5 rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50">
                  <Trash2 className="h-4 w-4" /> Delete
                </button>
              )}
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {canManage && row.status === "pending" && (
                <button type="button" className="btn btn-primary gap-1.5" disabled={blockers.length > 0 || approve.isPending} onClick={() => approve.mutate(row)}>
                  <CheckCircle2 className="h-4 w-4" /> {approve.isPending ? "Promoting…" : `Promote to ${row.to_class_name}`}
                </button>
              )}
            </>
          }
        >
          <Checks checks={promotionChecks(row, rowCriteria)} />
          {canManage && <Blockers items={blockers} />}
          {approve.isError && <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{apiErrorMessage(approve.error, "The promotion could not be approved.")}</p>}
          {canManage && row.status === "pending" && (
            <ReasonPanel
              title="Reject the proposal"
              description="The learner stays in their class. The reason is kept on their record."
              placeholder="Why the learner is not being promoted"
              context={{ key: "students.lifecycle_note", params: { kind: "promotion_reject", id: row.id } }}
              button="Reject proposal"
              danger
              busy={reject.isPending}
              error={reject.error}
              errorText="The proposal could not be rejected."
              onSubmit={(reason) => reject.mutate({ row, reason })}
            />
          )}
          <LearnerHistory studentId={row.student} />
        </DetailsModal>
      )}

      {proposing && (
        <ProposeClassForm
          criteria={criteria.filter((item) => item.is_active)}
          onClose={onCloseProposal}
          onDone={(text) => { onCloseProposal(); setNotice({ tone: "success", text }); onChanged(); }}
        />
      )}
    </div>
  );
}

type Draft = { ticked: boolean; attendance: string; average: string; incidents: string };

/**
 * Propose a whole class at once. Each learner's attendance, average and
 * incidents are read from the registers, published results and discipline
 * records; a figure is changed only where the records are incomplete.
 */
function ProposeClassForm({ criteria, onClose, onDone }: { criteria: PromotionCriteria[]; onClose: () => void; onDone: (text: string) => void }) {
  const queryClient = useQueryClient();
  const [criteriaId, setCriteriaId] = useState(criteria.length === 1 ? String(criteria[0].id) : "");
  const [drafts, setDrafts] = useState<Record<number, Draft>>({});
  const [notes, setNotes] = useState("");
  const candidates = useQuery({ queryKey: ["lifecycle", "candidates", criteriaId], queryFn: () => lifecycleService.getPromotionCandidates(Number(criteriaId)), enabled: Boolean(criteriaId) });
  const chosen = criteria.find((item) => String(item.id) === criteriaId);
  const learners = candidates.data?.learners ?? [];

  useEffect(() => {
    setDrafts(Object.fromEntries(learners.map((row) => [row.id, {
      ticked: !row.proposal && row.meets_criteria,
      attendance: String(Number(row.attendance_percentage)),
      average: String(Number(row.average_grade)),
      incidents: String(row.behavior_incidents),
    }])));
    // Reset only when a new class is loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidates.data]);

  const change = (id: number, changes: Partial<Draft>) => setDrafts((current) => ({ ...current, [id]: { ...current[id], ...changes } }));
  const ticked = learners.filter((row) => drafts[row.id]?.ticked && !row.proposal);
  const edited = (id: number) => {
    const row = learners.find((item) => item.id === id)!;
    const draft = drafts[id];
    return draft && (Number(draft.attendance) !== Number(row.attendance_percentage) || Number(draft.average) !== Number(row.average_grade) || Number(draft.incidents) !== row.behavior_incidents);
  };
  const propose = useMutation({
    mutationFn: () => lifecycleService.bulkPromotion({
      student_ids: ticked.map((row) => row.id),
      from_class_id: chosen!.from_class,
      to_class_id: chosen!.to_class,
      academic_year_id: candidates.data!.academic_year,
      notes: notes.trim(),
      measures: ticked.filter((row) => edited(row.id)).map((row) => ({
        student: row.id,
        attendance_percentage: Number(drafts[row.id].attendance),
        average_grade: Number(drafts[row.id].average),
        behavior_incidents: Number(drafts[row.id].incidents),
      })),
    }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["lifecycle", "candidates"] });
      onDone(`${result.promotions.length} ${result.promotions.length === 1 ? "learner" : "learners"} proposed for promotion from ${chosen!.from_class_name} to ${chosen!.to_class_name}.`);
    },
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (ticked.length) propose.mutate();
  };
  const places = candidates.data?.destination_places;

  return (
    <FormModal
      id="propose-class-form"
      size="4xl"
      title="Propose a class for promotion"
      busy={propose.isPending}
      error={propose.isError ? apiErrorMessage(propose.error, "The proposals could not be made.") : undefined}
      saveLabel={`Propose ${ticked.length} ${ticked.length === 1 ? "learner" : "learners"}`}
      note="Nobody moves class until each proposal is approved."
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="Class" divided={false} columns={1}>
        <Field label="Promotion" hint={criteria.length ? "Which class moves up, into which class, under which criteria." : undefined}>
          <select required value={criteriaId} onChange={(event) => setCriteriaId(event.target.value)} className="input mt-1">
            <option value="" disabled>{criteria.length ? "Choose a class" : "Set up promotion criteria in Settings first"}</option>
            {criteria.map((item) => (
              <option key={item.id} value={item.id}>{item.from_class_name} → {item.to_class_name} · {item.name}</option>
            ))}
          </select>
        </Field>
        {chosen && (
          <p className="text-xs text-gray-500">
            Needs at least {pct(chosen.min_attendance_percentage)} attendance and a {pct(chosen.min_average_grade)} average, with no more than {chosen.max_behavior_incidents} behaviour incidents.
            {chosen.requires_parent_approval ? " A guardian must agree before each promotion is approved." : ""}
          </p>
        )}
      </FormSection>

      {criteriaId && (
        <FormSection title="Learners" description="Figures come from the day registers, published exam results and discipline records for the year. Change one only where the records are incomplete; changed figures are marked." columns={1}>
          {candidates.isLoading ? (
            <div className="space-y-1.5">{Array.from({ length: 5 }).map((_, index) => <div key={index} className="h-8 animate-pulse rounded bg-gray-100" />)}</div>
          ) : candidates.isError ? (
            <p className="text-sm text-red-700">{apiErrorMessage(candidates.error, "The class could not be loaded.")}</p>
          ) : !learners.length ? (
            <p className="rounded-lg border border-dashed border-gray-300 py-6 text-center text-sm text-gray-500">No learners are in {chosen?.from_class_name} now.</p>
          ) : (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <span className="text-gray-600">{ticked.length} of {learners.filter((row) => !row.proposal).length} chosen{typeof places === "number" ? ` · ${places} ${places === 1 ? "place" : "places"} free in ${chosen?.to_class_name}` : ""}</span>
                <span className="flex gap-3">
                  <button type="button" className="text-xs font-medium text-primary-700 hover:underline" onClick={() => setDrafts((current) => Object.fromEntries(Object.entries(current).map(([id, draft]) => [id, { ...draft, ticked: Boolean(learners.find((row) => row.id === Number(id) && !row.proposal && row.meets_criteria)) }])))}>Choose those who meet it</button>
                  <button type="button" className="text-xs font-medium text-primary-700 hover:underline" onClick={() => setDrafts((current) => Object.fromEntries(Object.entries(current).map(([id, draft]) => [id, { ...draft, ticked: false }])))}>Clear</button>
                </span>
              </div>
              {typeof places === "number" && ticked.length > places && (
                <p className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {chosen?.to_class_name} has {places} places free; approvals stop once it is full. Raise its places in Classes & Streams if needed.</p>
              )}
              <div className="sheet-wrap max-h-96 overflow-y-auto">
                <table className="sheet">
                  <thead>
                    <tr>
                      <th className="w-8"><span className="sr-only">Propose</span></th>
                      <th>Learner</th>
                      <th className="text-right">Attendance %</th>
                      <th className="text-right">Average %</th>
                      <th className="text-right">Incidents</th>
                      <th>Criteria</th>
                    </tr>
                  </thead>
                  <tbody>
                    {learners.map((row) => {
                      const draft = drafts[row.id];
                      if (!draft) return null;
                      const checks = chosen ? promotionChecks({ attendance_percentage: draft.attendance, average_grade: draft.average, behavior_incidents: Number(draft.incidents) }, chosen) : [];
                      const meets = checks.every((check) => check.ok);
                      const input = "input-compact w-20 text-right tabular-nums";
                      return (
                        <tr key={row.id} className={row.proposal ? "opacity-60" : undefined}>
                          <td>
                            <input type="checkbox" aria-label={`Propose ${row.full_name}`} disabled={Boolean(row.proposal)} checked={draft.ticked && !row.proposal} onChange={(event) => change(row.id, { ticked: event.target.checked })} className="h-4 w-4 accent-primary-600" />
                          </td>
                          <td>
                            <span className="font-medium text-gray-900">{row.full_name}</span> <span className="text-xs text-gray-500">{row.student_id}</span>
                            {row.proposal && <span className="block text-xs text-gray-500">Already proposed ({promotionStatus(row.proposal.status).label.toLowerCase()})</span>}
                            {edited(row.id) && <span className="block text-xs text-amber-700">Figures changed from the records</span>}
                          </td>
                          <td className="text-right">
                            <input type="number" min={0} max={100} step="0.01" aria-label={`Attendance for ${row.full_name}`} disabled={Boolean(row.proposal)} value={draft.attendance} onChange={(event) => change(row.id, { attendance: event.target.value })} className={input} />
                            <span className="block text-[11px] text-gray-400">{row.attendance_days ? `${row.attendance_days} days` : "no registers"}</span>
                          </td>
                          <td className="text-right">
                            <input type="number" min={0} max={100} step="0.01" aria-label={`Average for ${row.full_name}`} disabled={Boolean(row.proposal)} value={draft.average} onChange={(event) => change(row.id, { average: event.target.value })} className={input} />
                            <span className="block text-[11px] text-gray-400">{row.results_counted ? `${row.results_counted} results` : "no results"}</span>
                          </td>
                          <td className="text-right">
                            <input type="number" min={0} aria-label={`Incidents for ${row.full_name}`} disabled={Boolean(row.proposal)} value={draft.incidents} onChange={(event) => change(row.id, { incidents: event.target.value })} className={input} />
                          </td>
                          <td>{meets ? <span className="text-emerald-700">Meets</span> : <span className="text-red-700" title={checks.filter((check) => !check.ok).map((check) => check.label).join(", ")}>Falls short</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </FormSection>
      )}

      {criteriaId && (
        <FormSection title="Note for the decision" columns={1}>
          <Field as="div" label="Note" optional>
            <AIAssistField
              rows={2}
              value={notes}
              onChange={(next) => setNotes(next)}
              placeholder="e.g. End of year review; figures from Term 3 reports."
              context={{ key: "students.lifecycle_note", params: { kind: "promotion_notes", id: Number(criteriaId), count: ticked.length } }}
              draftLabel="Draft note"
              draftHint="Uses the class move and its criteria. It never adds facts you did not give."
            />
          </Field>
        </FormSection>
      )}
    </FormModal>
  );
}
